import { FetchResult, useMutation } from '@apollo/client';
import { PORTAL_DOCUMENT } from '../views/Accounts/graphql/portalDocument';

// The application identifier the impersonation backend keys sessions on for this app.
const APPLICATION = 'Portal';
const METHOD = 'post';

enum IDocType {
  GENERATE_IMPERSONATION_PASSWORD = 'generateImpersonationPassword',
  VERIFY_IMPERSONATION_PASSWORD = 'verifyImpersonationPassword',
  IMPERSONATE_USER_LOGIN = 'impersonateUserLogin',
  LOGOUT_IMPERSONATION = 'logoutImpersonation',
}

interface IVerifyImpersonationParams {
  password: string;
}

interface IVerifyImpersonationResponse {
  status: string;
  errorMessage: string;
}

interface IImpersonateUserLoginParams {
  impersonatedEmailAddress: string;
}

interface IImpersonateUserLoginResponse {
  status: string;
  errorMessage: string;
  result: {
    userToken: string;
    impersonatorId: number;
    targetUserId: string;
    sessionExpiresAt: string;
    sessionStatus: string;
  };
}

interface ILogoutImpersonationParams {
  impersonatorId: number;
  targetUserId: string;
}

interface ILogoutImpersonationResponse {
  status: string;
  errorMessage: string;
}

interface IGenerateImpersonationParams {
  rmEmailAddress: string;
}

interface IGenerateImpersonationResponse {
  status: string;
  errorMessage: string;
  result: {
    rmEmailAddress: string;
    password: string;
    generatedAt: string;
  };
}

// portalDocument returns a JSON blob shaped as { data: <payload>, ... }
type PortalDocumentResult = { portalDocument?: { data?: unknown } | null };

const getResponse = <T>(dataIn: FetchResult<PortalDocumentResult>) => {
  return (dataIn?.data?.portalDocument?.data ?? {}) as T;
};

export const useImpersonation = () => {
  const [impersonationFn] = useMutation<PortalDocumentResult>(PORTAL_DOCUMENT);

  const generateImpersonation = async ({ rmEmailAddress }: IGenerateImpersonationParams) => {
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.GENERATE_IMPERSONATION_PASSWORD,
        data: { rmEmailAddress, application: APPLICATION },
      },
    });

    return getResponse<IGenerateImpersonationResponse>(response);
  };

  const verifyImpersonation = async ({ password }: IVerifyImpersonationParams) => {
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.VERIFY_IMPERSONATION_PASSWORD,
        data: { password, application: APPLICATION },
      },
    });

    return getResponse<IVerifyImpersonationResponse>(response);
  };

  const impersonateLogin = async ({ impersonatedEmailAddress }: IImpersonateUserLoginParams) => {
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.IMPERSONATE_USER_LOGIN,
        data: { impersonatedEmailAddress, application: APPLICATION },
      },
    });

    return getResponse<IImpersonateUserLoginResponse>(response);
  };

  const impersonateLogout = async ({ impersonatorId, targetUserId }: ILogoutImpersonationParams) => {
    const response = await impersonationFn({
      variables: {
        method: METHOD,
        docType: IDocType.LOGOUT_IMPERSONATION,
        data: { impersonatorId, targetUserId, application: APPLICATION },
      },
    });

    return getResponse<ILogoutImpersonationResponse>(response);
  };

  return {
    generateImpersonation,
    verifyImpersonation,
    impersonateLogin,
    impersonateLogout,
  };
};
